import {
  Component,
  inject,
  signal,
  viewChild,
  DestroyRef,
  computed,
  ChangeDetectionStrategy,
  WritableSignal,
  Signal
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';

import { OrdersService } from '../orders/orders.service';
import { OrderFlowAuthService } from '../auth/order-flow-auth.service';
import { SidebarTooltipDirective } from '../shell/sidebar-tooltip.directive';
import { SpinnerComponent } from '../shared/spinner.component';
import { ToastComponent } from '../shared/toast.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'of-reports-nav-menu',
  imports: [SpinnerComponent, ToastComponent, SidebarTooltipDirective],
  template: `
    @if (showReports()) {
      <div class="text-muted mt-2">
        <span class="sidebar-heading">Reports</span>
      </div>
      <div id="sidebar-nav" class="list-group text-sm-start">
        <button
          class="list-group-item border-0 mb-1 p-1 text-start w-100 d-flex align-items-center"
          type="button"
          [disabled]="isGeneratingEod()"
          (click)="generateEod()"
          ofSidebarTooltip="EOD Summary"
        >
          <i class="bi bi-envelope-paper me-2"></i>
          <span class="flex-grow-1">EOD Summary</span>
          @if (isGeneratingEod()) {
            <span class="spinner-border spinner-border-sm" role="status"></span>
          }
        </button>
      </div>
      <div id="sidebar-nav" class="list-group text-sm-start">
        <button
          class="list-group-item border-0 mb-1 p-1 text-start w-100 d-flex align-items-center"
          type="button"
          [disabled]="isGeneratingGtcGtd()"
          (click)="generateGtcGtd()"
          ofSidebarTooltip="GTC/GTD Summary"
        >
          <i class="bi bi-clock me-2"></i>
          <span class="flex-grow-1">GTC/GTD Summary</span>
          @if (isGeneratingGtcGtd()) {
            <span class="spinner-border spinner-border-sm" role="status"></span>
          }
        </button>
      </div>
      <div id="sidebar-nav" class="list-group text-sm-start">
        <button
          class="list-group-item border-0 mb-1 p-1 text-start w-100 d-flex align-items-center"
          type="button"
          [disabled]="isGeneratingMaturingTimeDeposits()"
          (click)="generateMaturingTimeDeposits()"
          ofSidebarTooltip="Time Deposits Reminder"
        >
          <i class="bi bi-calendar-check me-2"></i>
          <span class="flex-grow-1">TD Reminder</span>
          @if (isGeneratingMaturingTimeDeposits()) {
            <span class="spinner-border spinner-border-sm" role="status"></span>
          }
        </button>
      </div>
    }

    <of-toast #toastEodProgress [showCloseButton]="false">
      <div id="title" class="d-flex align-items-center gap-2">
        <of-spinner [diameter]="14"></of-spinner>
        <span>Generating EOD Summary</span>
      </div>
      <div id="content" class="of-toast-notify__body">Sending EOD Trade Summary email</div>
    </of-toast>

    <of-toast #toastEodSuccess [timeout]="4000">
      <div id="title" class="d-flex align-items-center gap-2">
        <i class="bi bi-check-circle-fill of-toast-notify__success-icon"></i>
        <span>EOD Summary Sent</span>
      </div>
      <div id="content" class="of-toast-notify__body">EOD Trade Summary email generated successfully.</div>
    </of-toast>

    <of-toast #toastGtcGtdProgress [showCloseButton]="false">
      <div id="title" class="d-flex align-items-center gap-2">
        <of-spinner [diameter]="14"></of-spinner>
        <span>Generating GTC/GTD Summary</span>
      </div>
      <div id="content" class="of-toast-notify__body">Sending GTC/GTD Summary email</div>
    </of-toast>

    <of-toast #toastGtcGtdSuccess [timeout]="4000">
      <div id="title" class="d-flex align-items-center gap-2">
        <i class="bi bi-check-circle-fill of-toast-notify__success-icon"></i>
        <span>GTC/GTD Summary Sent</span>
      </div>
      <div id="content" class="of-toast-notify__body">GTC/GTD Summary email generated successfully.</div>
    </of-toast>

    <of-toast #toastMaturingProgress [showCloseButton]="false">
      <div id="title" class="d-flex align-items-center gap-2">
        <of-spinner [diameter]="14"></of-spinner>
        <span>Generating Maturing Time Deposits</span>
      </div>
      <div id="content" class="of-toast-notify__body">Sending Maturing Time Deposits email</div>
    </of-toast>

    <of-toast #toastMaturingSuccess [timeout]="4000">
      <div id="title" class="d-flex align-items-center gap-2">
        <i class="bi bi-check-circle-fill of-toast-notify__success-icon"></i>
        <span>Maturing Time Deposits Sent</span>
      </div>
      <div id="content" class="of-toast-notify__body">Maturing Time Deposits email generated successfully.</div>
    </of-toast>
  `
})
export class ReportsNavMenuComponent {
  private readonly ordersService = inject(OrdersService);
  private readonly authService = inject(OrderFlowAuthService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly loggedIn = toSignal(this.authService.loggedIn$, { initialValue: false });

  protected readonly showReports = computed(() => this.loggedIn() && !this.authService.isRestrictedFxUser());

  protected readonly isGeneratingEod = signal(false);
  protected readonly isGeneratingGtcGtd = signal(false);
  protected readonly isGeneratingMaturingTimeDeposits = signal(false);

  protected readonly toastEodProgress = viewChild<ToastComponent>('toastEodProgress');
  protected readonly toastEodSuccess = viewChild<ToastComponent>('toastEodSuccess');
  protected readonly toastGtcGtdProgress = viewChild<ToastComponent>('toastGtcGtdProgress');
  protected readonly toastGtcGtdSuccess = viewChild<ToastComponent>('toastGtcGtdSuccess');
  protected readonly toastMaturingProgress = viewChild<ToastComponent>('toastMaturingProgress');
  protected readonly toastMaturingSuccess = viewChild<ToastComponent>('toastMaturingSuccess');

  protected generateEod(): void {
    this.runReport(
      this.ordersService.generateEodTradeSummaryEmail$(),
      this.isGeneratingEod,
      this.toastEodProgress,
      this.toastEodSuccess
    );
  }

  protected generateGtcGtd(): void {
    this.runReport(
      this.ordersService.generateGtcGtdSummaryEmail$(),
      this.isGeneratingGtcGtd,
      this.toastGtcGtdProgress,
      this.toastGtcGtdSuccess
    );
  }

  protected generateMaturingTimeDeposits(): void {
    this.runReport(
      this.ordersService.generateMaturingTimeDepositsEmail$(),
      this.isGeneratingMaturingTimeDeposits,
      this.toastMaturingProgress,
      this.toastMaturingSuccess
    );
  }

  private runReport(
    source$: Observable<void>,
    isGenerating: WritableSignal<boolean>,
    progressToast: Signal<ToastComponent | undefined>,
    successToast: Signal<ToastComponent | undefined>
  ): void {
    isGenerating.set(true);
    progressToast()?.show();
    source$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        progressToast()?.hide();
        isGenerating.set(false);
        successToast()?.show();
      },
      error: () => {
        progressToast()?.hide();
        isGenerating.set(false);
      }
    });
  }
}
